import { TOOL_COUNT } from '../../content/toolCount';
/**
 * tutorialConfigs — the per-video content registry that drives the /tutorials
 * hub and the shared /tutorials/<slug>/ video-page template.
 *
 * Design doc: agent_docs/cel1157_tutorials_hub_design.md (rev 2, team-signed).
 * Content source: Maeve's RATIFIED metadata map (CEL-1156/CEL-1160, Aug 12) —
 * slugs, titles, seoTitles, descriptions, answerBlocks, ffprobe durations,
 * source-filename stems all come from that map. Slugs are PERMANENT once
 * shipped (Rhea's ruling) — never invent or edit one here.
 * NOTE: rows 8 + 14 carry Rhea's ratified renames (`general-channel`,
 * `waiting-on-you-lane` — leading articles trimmed, her Aug-12 pass).
 *
 * ONE entry = one tutorial. Publishing video #N (flipping it live):
 *   1. Maeve's CEL-1127 ship-gate hands over the locked assets (md5-verified
 *      vs Drive INDEX.md + faststart-checked): place them at
 *      public/assets/tutorials/<slug>.mp4 / <slug>.en.vtt / <slug>-poster.jpg.
 *   2. Run scripts/generate_tutorial_transcripts.py (regenerates
 *      tutorialTranscripts.js from the VTTs — checked in, design doc §7.3).
 *   3. Flip `isLive: true` — this ONE flag gates the route, both sitemaps,
 *      the hub card, series counts, and up-next rails (D4). Set `uploadDate`
 *      to the deploy date (page-live date, NOT the cut's render date — R1).
 *   4. Add "/tutorials/<slug>" to reactSnap.include in package.json and to
 *      STATIC_PAGES in py/cellcog/api/seo.py (live entries only).
 *
 * CONTENT RULES: copy is verbatim from the ratified map (no em dashes,
 * "about a minute" never "two-minute"); durationSeconds = the map's ffprobe
 * values (the v1 mock chips are stale for 5 revised cuts).
 */

/** The 5 series, journey order. `id` doubles as the hub anchor slug
 *  (/tutorials/#getting-started) — STABLE: never change an id even if the
 *  label is reworded (anchors accumulate inbound links — R1). */
export const SERIES = [
  {
    id: 'getting-started',
    label: 'Getting started',
    blurb: 'Hire, choose how they work, and watch day one happen.',
    order: 1,
  },
  {
    id: 'channels',
    label: 'Channels',
    blurb: 'Where your team talks: topics, threads, mentions and history.',
    order: 2,
  },
  {
    id: 'tasks',
    label: 'Tasks',
    blurb: 'Delegate in chat, set rhythms, and answer what needs you.',
    order: 3,
  },
  {
    id: 'email',
    label: 'Email',
    blurb: 'Every employee comes with a real inbox for the outside world.',
    order: 4,
  },
  {
    id: 'dashboards-memory-reports',
    label: 'Dashboards, memory and reports',
    blurb: 'See the work, and the continuity behind it.',
    order: 5,
  },
];

/** Asset path helpers — one naming convention, applied everywhere. */
const mp4 = (slug) => `/assets/tutorials/${slug}.mp4?v=1`;
const vtt = (slug) => `/assets/tutorials/${slug}.en.vtt?v=1`;
const poster = (slug) => `/assets/tutorials/${slug}-poster.jpg?v=1`;

/**
 * TUTORIAL_CONFIGS — one entry per tutorial, journey order within series.
 * `sourceFile` = the final Drive cut (content-hash stem) Maeve's publish gate
 * copies bytes from — keeps config and ship-gate keyed to the same bytes.
 * ALL entries ship isLive:false; each flips true when its bytes land.
 */
export const TUTORIAL_CONFIGS = [
  // ---- Getting started ----
  {
    slug: 'hire-your-first-ai-employee',
    title: 'Hire your first AI employee',
    seoTitle: 'Hire Your First AI Employee in About a Minute | CellCog Tutorials',
    description: 'Hire an AI employee in about a minute: pick the role, write the brief like you would for a real hire, choose how they work, and watch day one start.',
    series: 'getting-started',
    order: 1,
    durationSeconds: 31,
    isLive: true, // DRAFT bytes (md5 bd9ced7e…) 2026-08-12 — swap on lock verdict
    mp4: mp4('hire-your-first-ai-employee'),
    vtt: vtt('hire-your-first-ai-employee'),
    thumbnail: poster('hire-your-first-ai-employee'),
    uploadDate: '2026-08-12T00:00:00-04:00',
    sourceFile: 'tutorial_hire_first_employee_product_v3_bd9ced7e.mp4',
    answerBlock: 'Hiring an AI employee on CellCog takes about a minute. Click New AI Employee, describe the role in plain language like you\'d brief a real hire, and pick how they work: on a schedule or on demand. Your new employee joins the team and starts day one on their own, no training or setup required.',
  },
  {
    slug: 'schedule-or-on-demand',
    title: 'Scheduled or on-demand',
    seoTitle: 'Scheduled vs On-Demand AI Employees | CellCog Tutorials',
    description: 'AI employees work two ways: on a schedule, clocking in like a full-time hire, or on demand, running only when you say. See it, change it, anytime.',
    series: 'getting-started',
    order: 2,
    durationSeconds: 38,
    isLive: true, // DRAFT bytes (md5 1fbd6a2e…) 2026-08-12 — swap on lock verdict
    mp4: mp4('schedule-or-on-demand'),
    vtt: vtt('schedule-or-on-demand'),
    thumbnail: poster('schedule-or-on-demand'),
    uploadDate: '2026-08-12T00:00:00-04:00',
    sourceFile: 'tutorial_schedule_vs_ondemand_product_v2_1fbd6a2e.mp4',
    answerBlock: 'CellCog AI employees work in one of two modes. A schedule means they clock in on their own, like a full-time hire; on demand means nothing runs until you press start. You can see the mode on the employee\'s page and change it anytime: pause, start a shift, or adjust the schedule. Nothing runs without you knowing.',
  },
  {
    slug: 'ai-employee-day-one',
    title: 'What day one looks like',
    seoTitle: 'An AI Employee\'s First Day | CellCog Tutorials',
    description: 'What an AI employee actually does on day one: onboards itself, puts tasks on your board, researches your company, and reports back. No ramp-up.',
    series: 'getting-started',
    order: 3,
    durationSeconds: 35,
    isLive: true, // Nitish's chat lock verdict 2026-08-12; bytes verified (md5 2e22277d…)
    mp4: mp4('ai-employee-day-one'),
    vtt: vtt('ai-employee-day-one'),
    thumbnail: poster('ai-employee-day-one'),
    uploadDate: '2026-08-12T00:00:00-04:00',
    sourceFile: 'tutorial_day_one_product_v1_2e22277d.mp4',
    answerBlock: 'On day one, a CellCog AI employee onboards itself. It puts its starting tasks on your board, researches your company, registers its identity, and sets up its own email address. Day one ends with a report covering what it learned and the few things it needs from you. There is no training period: day one is a working day.',
  },
  {
    slug: 'pick-the-right-mode',
    title: 'Pick the right mode',
    seoTitle: 'Pick the Right Mode and Tier | CellCog Tutorials',
    description: 'Every chat runs on a mode and a tier. Agent for everyday work, Creative for taste, Team for deep research; Flash, Core and Max set the depth.',
    series: 'getting-started',
    order: 4,
    durationSeconds: 69,
    isLive: true, // DRAFT bytes (md5 73cc3680…) 2026-08-21 — swap on lock verdict
    mp4: mp4('pick-the-right-mode'),
    vtt: vtt('pick-the-right-mode'),
    thumbnail: poster('pick-the-right-mode'),
    uploadDate: '2026-08-21T00:00:00-04:00',
    sourceFile: 'tutorial_modes_product_v7_73cc3680.mp4',
    answerBlock: 'Every CellCog chat runs at a mode and a tier you pick in the selector. Agent Mode is the everyday workhorse, Agent Creative is tuned for taste and design work, and Agent Team puts several agents on deep research. Tiers set the depth: Flash runs cheaper for easy, everyday tasks, while Core and Max spend more to think deeper for serious work. You can switch mode and tier anytime, mid-chat.',
    // Optional deep-reference link rendered after the answerBlock (CEL-1782: the one honest tutorial->guide mapping).
    guideHref: '/support/chat-guide',
    guideLinkText: 'Read the full Chat Guide: every mode, tier, and switching rule',
  },
  {
    slug: 'connect-your-tools',
    title: 'Connect your tools',
    seoTitle: 'Connect Your Tools (' + TOOL_COUNT + ' Apps) | CellCog Tutorials',
    description: 'Three ways to connect: Attach pulls files in, Delegate brings CellCog to your platforms, and Empower lets employees act across ' + TOOL_COUNT + ' apps.',
    series: 'getting-started',
    order: 5,
    durationSeconds: 92,
    isLive: true, // DRAFT bytes (md5 724c98d2…) 2026-08-21 — swap on lock verdict
    mp4: mp4('connect-your-tools'),
    vtt: vtt('connect-your-tools'),
    thumbnail: poster('connect-your-tools'),
    uploadDate: '2026-08-21T00:00:00-04:00',
    sourceFile: 'tutorial_connect_tools_product_v2_724c98d2.mp4',
    answerBlock: 'CellCog connects to your tools three ways. Attach pulls your files and data into chats, from platforms like Google Drive. Delegate brings CellCog into the tools your team already lives in, like Linear and your coding agents. Empower connects ' + TOOL_COUNT + ' apps with more than 20,000 tools so your employees can act on your behalf, with auto-approve guardrails you set. Connect once, and every chat can use it.',
  },
  // ---- Channels ----
  {
    slug: 'create-a-channel',
    title: 'Create a channel',
    seoTitle: 'How to Create a Channel | CellCog Tutorials',
    description: 'Create a channel for a new project or topic in seconds: click the plus, name it, post the kickoff, and pull in the employees who need it.',
    series: 'channels',
    order: 1,
    durationSeconds: 28,
    isLive: true, // bytes verified vs Drive lock registry (md5 bf737752…) 2026-08-12
    mp4: mp4('create-a-channel'),
    vtt: vtt('create-a-channel'),
    thumbnail: poster('create-a-channel'),
    uploadDate: '2026-08-12T00:00:00-04:00',
    sourceFile: 'tutorial_create_channel_product_v1_bf737752.mp4',
    answerBlock: 'To create a channel in CellCog, click the plus next to Channels in your sidebar, give it a name, and post the kickoff message. Pull in the employees who need it; every employee in your org can find the channel once it\'s live. One channel per project or topic keeps team knowledge organized.',
  },
  {
    slug: 'posts-and-threads',
    title: 'Posts and threads',
    seoTitle: 'Posts and Threads | CellCog Tutorials',
    description: 'Posts and threads keep team discussions tidy: updates go in the channel, replies stay in threads, and every discussion keeps its context.',
    series: 'channels',
    order: 2,
    durationSeconds: 31,
    isLive: true, // bytes verified vs Drive lock registry (md5 9e36bea6…) 2026-08-12
    mp4: mp4('posts-and-threads'),
    vtt: vtt('posts-and-threads'),
    thumbnail: poster('posts-and-threads'),
    uploadDate: '2026-08-12T00:00:00-04:00',
    sourceFile: 'tutorial_post_thread_product_v1_9e36bea6.mp4',
    answerBlock: 'In CellCog channels, anyone can post an update, human or AI employee. To respond without cluttering the channel, reply in a thread: replies stay attached to the original post, so the channel stays readable while deep dives keep their context.',
  },
  {
    slug: 'mention-your-ai-employee',
    title: 'Mentions that wake your employee',
    seoTitle: 'Mentions That Wake Your AI Employee | CellCog Tutorials',
    description: 'An @mention wakes your AI employee right away; a plain-text name waits for their next shift. Urgent gets attention now, the rest waits politely.',
    series: 'channels',
    order: 3,
    durationSeconds: 43,
    isLive: true, // DRAFT bytes (md5 1a023987…) 2026-08-12 — swap on lock verdict
    mp4: mp4('mention-your-ai-employee'),
    vtt: vtt('mention-your-ai-employee'),
    thumbnail: poster('mention-your-ai-employee'),
    uploadDate: '2026-08-12T00:00:00-04:00',
    sourceFile: 'tutorial_mentions_product_v1_1a023987.mp4',
    answerBlock: 'Mentioning an AI employee with the @ sign wakes them up immediately: they come online, read the message, and get to work. For anything that isn\'t urgent, write their name in plain text instead; they\'ll see it at the start of their next shift. Urgent gets attention now, everything else waits politely.',
  },
  {
    slug: 'dms-vs-channels',
    title: 'DMs vs channels',
    seoTitle: 'DMs vs Channels | CellCog Tutorials',
    description: 'Channels are for topics, DMs are for people. Private rooms with one person or several, humans or AI employees; shared knowledge stays in channels.',
    series: 'channels',
    order: 4,
    durationSeconds: 49,
    isLive: true, // DRAFT bytes (md5 4e7eae59…) 2026-08-12 — swap on lock verdict
    mp4: mp4('dms-vs-channels'),
    vtt: vtt('dms-vs-channels'),
    thumbnail: poster('dms-vs-channels'),
    uploadDate: '2026-08-12T00:00:00-04:00',
    sourceFile: 'tutorial_dms_vs_channels_product_v3_4e7eae59.mp4',
    answerBlock: 'The rule is simple: channels are for topics, direct messages are for people. Start a DM from the plus next to Direct Messages and pick one or several participants, humans or AI employees; it stays private to the people in it. When the answer could help anyone else, post in a channel instead, so knowledge stays shared.',
  },
  {
    // Ratified rename (Rhea's Aug-12 pass): leading article trimmed from the
    // map's original `the-general-channel`.
    slug: 'general-channel',
    title: 'One room for everyone: #general',
    seoTitle: 'The #general Channel | CellCog Tutorials',
    description: 'Every workspace starts with #general: one automatic channel the whole org sees, where announcements land and stay on the record.',
    series: 'channels',
    order: 5,
    durationSeconds: 32,
    isLive: true, // DRAFT bytes (md5 55c8c268…) 2026-08-12 — swap on lock verdict
    mp4: mp4('general-channel'),
    vtt: vtt('general-channel'),
    thumbnail: poster('general-channel'),
    uploadDate: '2026-08-12T00:00:00-04:00',
    sourceFile: 'tutorial_general_product_v1_55c8c268.mp4',
    answerBlock: 'Every CellCog workspace starts with #general, one channel the whole org sees. It\'s created automatically and every employee you hire is already in it. Post company-wide announcements there: every employee sees them on their next shift, and they stay on the record.',
  },
  {
    slug: 'catch-up-on-what-you-missed',
    title: 'Catch up on what you missed',
    seoTitle: 'Catch Up on What You Missed | CellCog Tutorials',
    description: 'Been away? Unread badges show what\'s new, threads keep the noise contained, and even a busy channel catches you up in seconds.',
    series: 'channels',
    order: 6,
    durationSeconds: 33,
    isLive: true, // DRAFT bytes (md5 4d1abe0d…) 2026-08-12 — swap on lock verdict
    mp4: mp4('catch-up-on-what-you-missed'),
    vtt: vtt('catch-up-on-what-you-missed'),
    thumbnail: poster('catch-up-on-what-you-missed'),
    uploadDate: '2026-08-12T00:00:00-04:00',
    sourceFile: 'tutorial_catch_up_product_v2_4d1abe0d.mp4',
    answerBlock: 'After time away, unread badges mark the channels with something new since your last visit. Open one and read from where you left off; replies stay tucked into threads, so even a busy channel catches you up in seconds rather than scrolling. Step away for a day, catch up in a minute.',
  },
  {
    slug: 'archive-a-channel',
    title: 'Archive a finished project',
    seoTitle: 'Archive a Channel | CellCog Tutorials',
    description: 'Archive a finished project\'s channel: it leaves the active list, keeps every message, and restores in one click if the work comes back.',
    series: 'channels',
    order: 7,
    durationSeconds: 26,
    isLive: true, // DRAFT bytes (md5 e13562a0…) 2026-08-12 — swap on lock verdict
    mp4: mp4('archive-a-channel'),
    vtt: vtt('archive-a-channel'),
    thumbnail: poster('archive-a-channel'),
    uploadDate: '2026-08-12T00:00:00-04:00',
    sourceFile: 'tutorial_archive_product_v1_e13562a0.mp4',
    answerBlock: 'When a project finishes, archive its channel. Archiving removes it from the active list but keeps every message; nothing is deleted, and you can browse archived channels anytime. If the project comes back, restore the channel in one click with its history intact.',
  },
  {
    slug: 'ai-employee-status-updates',
    title: 'Employees post their own updates',
    seoTitle: 'AI Employees Post Their Own Updates | CellCog Tutorials',
    description: 'AI employees post their own progress updates while you\'re away, so the team stays current without anyone asking for a status.',
    series: 'channels',
    order: 8,
    durationSeconds: 34,
    isLive: true, // DRAFT bytes (md5 ce87ac9d…) 2026-08-12 — swap on lock verdict
    mp4: mp4('ai-employee-status-updates'),
    vtt: vtt('ai-employee-status-updates'),
    thumbnail: poster('ai-employee-status-updates'),
    uploadDate: '2026-08-12T00:00:00-04:00',
    sourceFile: 'tutorial_employee_posts_product_v2_ce87ac9d.mp4',
    answerBlock: 'CellCog AI employees post their own updates while you\'re away. When one finishes a piece of work, it posts the update to the channel without being asked; next time you open CellCog, the unread badge is waiting. Read it, reply in a thread, and the employee picks your reply up on its next shift.',
  },
  // ---- Tasks ----
  {
    slug: 'delegate-work-in-chat',
    title: 'Delegate work in chat',
    seoTitle: 'Delegate Work in Chat | CellCog Tutorials',
    description: 'Delegate by chatting: ask your employee like a colleague, and the request becomes a task on the board, picked up on their next shift.',
    series: 'tasks',
    order: 1,
    durationSeconds: 32,
    isLive: true, // DRAFT bytes (md5 12ef54d5…) 2026-08-12 — swap on lock verdict
    mp4: mp4('delegate-work-in-chat'),
    vtt: vtt('delegate-work-in-chat'),
    thumbnail: poster('delegate-work-in-chat'),
    uploadDate: '2026-08-12T00:00:00-04:00',
    sourceFile: 'tutorial_give_task_product_v1_12ef54d5.mp4',
    answerBlock: 'To delegate work to an AI employee, just ask in their chat, the way you\'d message a colleague. The employee turns your request into a task on the shared board and picks it up on their next shift. The Tasks view shows every task across your whole team, organized by status.',
  },
  {
    slug: 'recurring-tasks',
    title: 'Recurring tasks',
    seoTitle: 'Recurring Tasks | CellCog Tutorials',
    description: 'Tell your employee a rhythm once and it becomes a recurring task that re-arms every time it\'s done. The board shows what\'s due today.',
    series: 'tasks',
    order: 2,
    durationSeconds: 25,
    isLive: true, // DRAFT bytes (md5 a83eded2…) 2026-08-12 — swap on lock verdict
    mp4: mp4('recurring-tasks'),
    vtt: vtt('recurring-tasks'),
    thumbnail: poster('recurring-tasks'),
    uploadDate: '2026-08-12T00:00:00-04:00',
    sourceFile: 'tutorial_recurring_tasks_product_v1_a83eded2.mp4',
    answerBlock: 'Tell your AI employee the rhythm once, weekly or daily or whatever the work needs, and they set it up as a recurring task on the board. Each time it\'s done, it re-arms for the next round, and the board shows what\'s due today. Set the rhythm once and the work keeps happening.',
  },
  {
    // Ratified rename (Rhea's Aug-12 pass): leading article trimmed from the
    // map's original `the-waiting-on-you-lane`.
    slug: 'waiting-on-you-lane',
    title: 'The Waiting on you lane',
    seoTitle: 'The Waiting on You Lane | CellCog Tutorials',
    description: 'Approvals, accounts, and decisions your employee needs from you land in one lane, with exactly what\'s needed and why. Answer it and work continues.',
    series: 'tasks',
    order: 3,
    durationSeconds: 27,
    isLive: true, // DRAFT bytes (md5 6a62a02d…) 2026-08-12 — swap on lock verdict
    mp4: mp4('waiting-on-you-lane'),
    vtt: vtt('waiting-on-you-lane'),
    thumbnail: poster('waiting-on-you-lane'),
    uploadDate: '2026-08-12T00:00:00-04:00',
    sourceFile: 'tutorial_waiting_on_you_product_v1_6a62a02d.mp4',
    answerBlock: 'When an AI employee needs something only you can give, an approval, an account, or a decision, that task lands in the Waiting on you lane. Open it to see exactly what they need and why; answer it and they take it from there on their next shift, so your employee doesn\'t stall, and your board only carries what genuinely needs you.',
  },
  {
    slug: 'feedback-on-a-task',
    title: 'Feedback on the task',
    seoTitle: 'Feedback on Tasks | CellCog Tutorials',
    description: 'Leave feedback as comments on the task itself, files and screenshots included. The conversation stays with the work, so context never gets lost.',
    series: 'tasks',
    order: 4,
    durationSeconds: 25,
    isLive: true, // DRAFT bytes (md5 72a77d30…) 2026-08-12 — swap on lock verdict
    mp4: mp4('feedback-on-a-task'),
    vtt: vtt('feedback-on-a-task'),
    thumbnail: poster('feedback-on-a-task'),
    uploadDate: '2026-08-12T00:00:00-04:00',
    sourceFile: 'tutorial_task_comments_product_v1_72a77d30.mp4',
    answerBlock: 'Feedback belongs on the task. Open any task, leave a comment, and your employee reads it on their next shift; attach files or screenshots if they help. The whole conversation stays with the task, so context never gets lost.',
  },
  // ---- Email ----
  {
    slug: 'ai-employee-email-inbox',
    title: 'Your employee has a real inbox',
    seoTitle: 'Your AI Employee\'s Email Inbox | CellCog Tutorials',
    description: 'Every AI employee has a real email address. Clients write in, the employee replies on its own, and anything needing you lands on your board.',
    series: 'email',
    order: 1,
    durationSeconds: 55,
    isLive: true, // DRAFT bytes (md5 4b8925af…) 2026-08-12 — swap on lock verdict
    mp4: mp4('ai-employee-email-inbox'),
    vtt: vtt('ai-employee-email-inbox'),
    thumbnail: poster('ai-employee-email-inbox'),
    uploadDate: '2026-08-12T00:00:00-04:00',
    sourceFile: 'tutorial_email_inbox_product_v1_4b8925af.mp4',
    answerBlock: 'Every CellCog AI employee comes with its own real email address, shown on the employee\'s page. Clients can email your employee directly; it reads, replies on its own, and anything that needs you lands on your board. Employees don\'t email each other, teammates talk in channels and DMs; email is for the outside world, and for you.',
  },
  // ---- Dashboards, memory and reports ----
  {
    slug: 'ai-employee-dashboards',
    title: 'Dashboards your employee maintains',
    seoTitle: 'AI Employee Dashboards | CellCog Tutorials',
    description: 'Your AI employee builds and maintains real dashboards: live numbers, read trends, and flags on what needs you, right beside the chat.',
    series: 'dashboards-memory-reports',
    order: 1,
    durationSeconds: 34,
    isLive: true, // DRAFT bytes (md5 f23faa5d…) 2026-08-12 — swap on lock verdict
    mp4: mp4('ai-employee-dashboards'),
    vtt: vtt('ai-employee-dashboards'),
    thumbnail: poster('ai-employee-dashboards'),
    uploadDate: '2026-08-12T00:00:00-04:00',
    sourceFile: 'tutorial_dashboards_product_v2_f23faa5d.mp4',
    answerBlock: 'Your AI employee builds dashboards and keeps them fresh; find them on the employee\'s page, right beside the chat. Open one full screen and it\'s a real board, not a screenshot. The employee doesn\'t just update the numbers: it reads the trends, spots what\'s moving, and flags what needs you.',
  },
  {
    slug: 'shift-handovers-and-memory',
    title: 'Shift handovers: they never forget',
    seoTitle: 'Shift Handovers and Memory | CellCog Tutorials',
    description: 'AI employees don\'t reset. Each shift ends with a handover to the next, so shift 50 remembers the decisions from shift 1.',
    series: 'dashboards-memory-reports',
    order: 2,
    durationSeconds: 31,
    isLive: true, // DRAFT bytes (md5 b0bd01c8…) 2026-08-12 — swap on lock verdict
    mp4: mp4('shift-handovers-and-memory'),
    vtt: vtt('shift-handovers-and-memory'),
    thumbnail: poster('shift-handovers-and-memory'),
    uploadDate: '2026-08-12T00:00:00-04:00',
    sourceFile: 'tutorial_shift_handovers_product_v1_b0bd01c8.mp4',
    answerBlock: 'CellCog AI employees don\'t reset between sessions. Work happens in shifts, and at the end of each one the employee writes a handover for the next, so the next shift picks up mid-thought with the same memory. Ask about something from last week and they know. That continuity is the difference between a chatbot and an employee.',
  },
  {
    slug: 'shift-reports',
    title: 'Every shift ends with a report',
    seoTitle: 'Shift Reports | CellCog Tutorials',
    description: 'Every shift ends with a short report in chat: what got done, what\'s next, and what they need from you. Catch up in seconds.',
    series: 'dashboards-memory-reports',
    order: 3,
    durationSeconds: 18,
    isLive: true, // DRAFT bytes (md5 53ef81b7…) 2026-08-12 — swap on lock verdict
    mp4: mp4('shift-reports'),
    vtt: vtt('shift-reports'),
    thumbnail: poster('shift-reports'),
    uploadDate: '2026-08-12T00:00:00-04:00',
    sourceFile: 'tutorial_shift_report_product_v1_53ef81b7.mp4',
    answerBlock: 'Every shift ends with a report delivered right in the chat: what got done, what\'s next, and what they need from you, in a few lines. You catch up in seconds and never wonder what your employee did today.',
  },
];

// ---- Derived views (single source of truth for every consumer) ----

/** Live entries only — the ONLY list routes/sitemaps/hub/up-next may see (D4). */
export const liveTutorials = () => TUTORIAL_CONFIGS.filter((t) => t.isLive);

/** All LIVE tutorials in global journey order (series order, then entry order). */
export const journeyOrdered = () => {
  const seriesOrder = new Map(SERIES.map((s) => [s.id, s.order]));
  return liveTutorials().sort(
    (a, b) => (seriesOrder.get(a.series) - seriesOrder.get(b.series)) || (a.order - b.order),
  );
};

/** Live tutorials for one series, in order. */
export const liveInSeries = (seriesId) =>
  liveTutorials().filter((t) => t.series === seriesId).sort((a, b) => a.order - b.order);

/** Look up one LIVE tutorial by slug (route validation — unknown/non-live → null). */
export const getLiveTutorial = (slug) => liveTutorials().find((t) => t.slug === slug) || null;

/** The next N live tutorials after `slug` in journey order (up-next rail; wraps). */
export const upNext = (slug, n = 4) => {
  const ordered = journeyOrdered();
  const idx = ordered.findIndex((t) => t.slug === slug);
  if (idx === -1) return ordered.slice(0, n);
  const out = [];
  for (let i = 1; i <= ordered.length - 1 && out.length < n; i += 1) {
    out.push(ordered[(idx + i) % ordered.length]);
  }
  return out;
};

/** mm:ss chip from durationSeconds. */
export const formatDuration = (s) =>
  `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

/** ISO-8601 duration for VideoObject schema (PT#M#S). */
export const isoDuration = (s) => `PT${Math.floor(s / 60)}M${Math.floor(s % 60)}S`;
